import React, { useEffect, useState } from 'react';
import Table from 'components/Table';
import {
  Avatar,
  Breadcrumb, Card, Skeleton, StatCard, Tag,
} from 'xland-ui';
import { useSelector } from 'react-redux';
import { formatDate, formatNumber } from 'utils/functions';
import { deleteInvestmentOperation, getInvestorAsset, getInvestorAssetOperations } from 'modules/investmentAssets/repository';
import { Pencil, PlusIcon, Trash } from 'lucide-react';
import { useCounter } from 'utils/customHooks';
import useDialog from 'components/Dialog/useDialog';
import { useParams } from 'react-router-dom';
import PopupEditOperation from './PopupEditOperation';

function InvestorInvestment() {
  const { id, organization } = useParams();
  const literalsAssets = useSelector(state => state.i18n.literals.investmentAssets);
  const literalsCommon = useSelector(state => state.i18n.literals.common);
  const literalsOnboarding = useSelector(state => state.i18n.literals.onboarding);
  const literals = { ...literalsAssets, ...literalsCommon, ...literalsOnboarding };
  const forceRefresh = useCounter();
  const { dialog } = useDialog();

  const [asset, setAsset] = useState();
  const [operations, setOperations] = useState();
  const [editOperation, setEditOperation] = useState(null);

  const deleteOperation = async (opId) => {
    const confirm = await dialog({
      type: 'confirmDanger',
      text: literals.operationRemove,
    });

    if (confirm) {
      await dialog({
        type: 'loading',
        execute: async () => {
          await deleteInvestmentOperation(id, organization, opId);
          forceRefresh.increase();
        },
      });
    }
  };

  useEffect(() => {
    (async () => {
      const aux = await getInvestorAsset(id);
      const org = aux.organizations.find(o => o.id === organization);
      setAsset({ ...aux.asset, organization: org });
    })();
  }, [id, organization]);

  const columns = [
    {
      field: 'date',
      label: literalsCommon.date,
      preRender: (v) => (v ? formatDate(v) : '-'),
      sortable: op => (op?.date ? new Date(op.operation.date).getTime() : null),
    },
    {
      field: 'status',
      label: literalsCommon.status,
      preRender: (v) => (v ? <Tag text={literals[v]} /> : '-'),
      sortable: true,
    },
    {
      field: 'invested',
      label: 'Invested',
      preRender: (v) => formatNumber(v, '-', { symbol: '€' }),
      sortable: true,
    },
    {
      field: 'realized',
      label: literals.realized,
      preRender: (v) => formatNumber(v, '-', { symbol: '€' }),
      sortable: true,
    },
    {
      field: 'realized',
      label: literals.unrealized,
      preRender: (realized, { value }) => formatNumber((value || 0) - (realized || 0), '-', { symbol: '€' }),
      sortable: (realized, { value }) => (value && realized ? value - realized : null),
    },
    {
      field: 'value',
      label: literalsCommon.value,
      preRender: (v) => formatNumber(v, '-', { symbol: '€' }),
      sortable: true,
    },
    {
      field: 'value',
      label: 'MOIC',
      preRender: (value, { invested }) => (value && invested ? formatNumber(value / invested, '-', { symbol: 'x' }) : '-'),
      sortable: (value, { invested }) => (value && invested ? (value / invested) : null),
    },
    {
      type: 'menu',
      buttons: [
        {
          icon: <Pencil />,
          onClick: row => setEditOperation(row),
          text: literalsCommon.edit,
          hidden: (_, row) => row.organization?.permissions < 2,
        },
        {
          icon: <Trash />,
          mode: 'danger',
          onClick: row => deleteOperation(row.id),
          text: literalsCommon.delete,
          hidden: (_, row) => row.organization?.permissions < 2,
        },
      ],
    },
  ];

  return (
    <>
      <Breadcrumb routes={[{ name: literals.investmentDetails }]} />
      <div className='d-flex align-items-center mb-3'>
        {!asset ? (
          <>
            <Skeleton width={82} height={82} />
            <div className='ms-4'>
              <div className='mb-3'>
                <Skeleton width='250px' height='24px' />
              </div>
              <Skeleton width='200px' height='17px' />
            </div>
          </>
        ) : (
          <>
            <Avatar
              src={asset?.logo}
              title={asset?.name}
              size={80}
            />
            <div className='ms-4'>
              <h2 className='m-0'>{asset?.name}</h2>
              <p className='m-0 fc-light'>{asset?.organization?.name}</p>
            </div>
          </>
        )}
      </div>
      <Card className='px-5 py-3 mb-4'>
        <div className='row g-3 p-3'>
          {!asset && Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className='col-12 col-sm-6 col-md-4'>
              <h6><Skeleton width='70%' height='17px' /></h6>
              <Skeleton width='30%' height='17px' />
            </div>
          ))}

          {asset && Object.entries(asset)
            .filter(([key]) => !['name', 'logo', 'createdAt', 'createdBy', 'id', '_searchScore', 'deletedAt', 'deletedBy', 'organization'].includes(key))
            .map(([key, value]) => value && (
              <div key={key} className='col-12 col-sm-6 col-md-4'>
                <h6 className='fw-b'>{literals[key] || key}</h6>
                <span>{literals[value] || value}</span>
              </div>
            ))}
        </div>
        {/* <div className='buttons'>
          <Button
            icon={<PlusIcon />}
            onClick={() => setEditOperation({})}
            text={literalsCommon.add}
          />
        </div> */}
      </Card>
      {
        operations?.items.length > 0 && (
          <div className='row mb-4'>
            <div className='col-12 col-sm-6 col-md-4'>
              <StatCard
                title={literals.value}
                value={formatNumber(operations?.items[0].value, '-', { symbol: '€' })}
                icon={<PlusIcon />}
              />
            </div>
            <div className='col-12 col-sm-6 col-md-4'>
              <StatCard
                title={literals.invested}
                value={formatNumber(operations?.items[0].invested, '-', { symbol: '€' })}
                icon={<PlusIcon />}
              />
            </div>
            <div className='col-12 col-sm-6 col-md-4'>
              <StatCard
                title={literals.multiple}
                value={formatNumber(operations?.items[0].value / operations?.items[0].invested, '-', { symbol: 'x' })}
                icon={<PlusIcon />}
              />
            </div>
          </div>
        )
      }

      <Table
        actions={[
          {
            icon: <PlusIcon />,
            onClick: () => setEditOperation({}),
            text: literalsCommon.add,
            hidden: asset?.organization?.permissions < 2,
          },
        ]}
        alert={{
          type: 'info',
          text: `${operations?.total} ${literals.operations}`,
          className: 'alert-investments',
        }}
        columns={columns}
        onClickRow={asset?.organization?.permissions >= 2 ? row => setEditOperation(row) : null}
        fetch={async (page, size) => {
          const aux = await getInvestorAssetOperations(id, organization, { page, size });
          setOperations(aux);
          return aux;
        }}
        forceFetch={forceRefresh.value}
      />
      {
        editOperation && (
          <PopupEditOperation
            asset={id}
            organization={organization}
            operation={editOperation}
            onClose={() => setEditOperation(null)}
            onSubmit={() => {
              forceRefresh.increase();
            }}
          />
        )
      }
    </>
  );
}

export default InvestorInvestment;
